<template>
  <section class="page-section">
    <div class="toolbar">
      <div class="toolbar-left">
        <el-input v-model="filters.search" placeholder="按员工账号搜索" clearable class="toolbar-input" />
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </div>
      <el-button type="primary" @click="dialogVisible = true">创建员工账号</el-button>
    </div>

    <el-table :data="employees.content" class="business-table">
      <el-table-column prop="username" label="账号" min-width="150" />
      <el-table-column prop="phone" label="联系电话" min-width="140" />
      <el-table-column prop="createTime" label="创建时间" min-width="180" />
      <el-table-column prop="roleLabel" label="角色" width="120" />
      <el-table-column prop="status" label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'">
            {{ row.statusLabel }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="row.role === 3"
            link
            :type="row.status === 1 ? 'danger' : 'primary'"
            @click="handleToggleStatus(row)"
          >
            {{ row.status === 1 ? '封禁' : '启用' }}
          </el-button>
          <span v-else class="muted-text">不可操作</span>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="employees.totalElements > 0" class="pager">
      <span class="pager-text">第 {{ filters.page }} / {{ totalPages }} 页</span>
      <el-button :disabled="filters.page <= 1" @click="handlePrevPage">
        <el-icon><ArrowLeft /></el-icon>
        上一页
      </el-button>
      <el-button type="primary" :disabled="filters.page >= totalPages" @click="handleNextPage">
        下一页
        <el-icon><ArrowRight /></el-icon>
      </el-button>
    </div>

    <el-dialog v-model="dialogVisible" title="创建员工账号" width="460px">
      <el-form ref="employeeFormRef" :model="employeeForm" :rules="employeeRules" label-position="top">
        <el-form-item label="员工账号" prop="username">
          <el-input v-model="employeeForm.username" />
        </el-form-item>
        <el-form-item label="初始密码" prop="password">
          <el-input v-model="employeeForm.password" type="password" show-password />
        </el-form-item>
        <el-form-item label="联系电话" prop="phone">
          <el-input v-model="employeeForm.phone" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEmployee">创建</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import {
  createAdminEmployee,
  fetchAdminEmployees,
  updateAdminEmployeeStatus
} from '../../services/adminService'

export default {
  name: 'AdminEmployeesPanel',
  components: {
    ArrowLeft,
    ArrowRight
  },
  data() {
    return {
      employees: {
        content: [],
        totalElements: 0
      },
      filters: {
        search: '',
        page: 1,
        pageSize: 10
      },
      dialogVisible: false,
      employeeForm: {
        username: '',
        password: '',
        phone: ''
      },
      employeeRules: {
        username: [{ required: true, message: '请输入员工账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入初始密码', trigger: 'blur' }],
        phone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }]
      }
    }
  },
  computed: {
    totalPages() {
      const total = Number(this.employees.totalElements) || 0
      const pageSize = Number(this.filters.pageSize) || 10

      return Math.max(1, Math.ceil(total / pageSize))
    }
  },
  mounted() {
    this.loadEmployees()
  },
  methods: {
    isCancelAction(error) {
      return error === 'cancel' || error === 'close'
    },
    async loadEmployees() {
      const response = await fetchAdminEmployees(this.filters)
      this.employees = response.data
    },
    handlePageChange(page) {
      this.filters.page = page
      this.loadEmployees()
    },
    handlePrevPage() {
      if (this.filters.page <= 1) {
        return
      }

      this.handlePageChange(this.filters.page - 1)
    },
    handleNextPage() {
      if (this.filters.page >= this.totalPages) {
        return
      }

      this.handlePageChange(this.filters.page + 1)
    },
    handleSearch() {
      this.filters.page = 1
      this.loadEmployees()
    },
    handleReset() {
      this.filters.search = ''
      this.filters.page = 1
      this.loadEmployees()
    },
    async submitEmployee() {
      const valid = await this.$refs.employeeFormRef.validate().catch(() => false)

      if (!valid) {
        return
      }

      try {
        const response = await createAdminEmployee(this.employeeForm)
        ElMessage.success(response.message || response.msg || '员工账号创建成功')
        this.dialogVisible = false
        this.employeeForm = { username: '', password: '', phone: '' }
        this.loadEmployees()
      } catch (error) {
        ElMessage.error(error.message)
      }
    },
    async handleToggleStatus(row) {
      const nextStatus = row.status === 1 ? 0 : 1
      const actionText = nextStatus === 0 ? '封禁' : '启用'

      try {
        await ElMessageBox.confirm(`确认${actionText}员工账号「${row.username}」吗？`, `${actionText}确认`, {
          type: 'warning'
        })
        const response = await updateAdminEmployeeStatus(row.id, nextStatus)
        ElMessage.success(response.message || response.msg || `${actionText}成功`)
        this.loadEmployees()
      } catch (error) {
        if (!this.isCancelAction(error)) {
          ElMessage.error(error.message || '操作已取消')
        }
      }
    }
  }
}
</script>

<style scoped>
.page-section {
  display: grid;
  gap: 22px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-input {
  width: 260px;
}

.business-table {
  border-radius: 22px;
  overflow: hidden;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.pager-text {
  color: #475569;
  font-size: 14px;
  font-weight: 600;
}

.muted-text {
  color: #94a3b8;
}

@media (max-width: 768px) {
  .toolbar-left,
  .toolbar-input {
    width: 100%;
  }
}
</style>
